import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiStar, FiTrendingUp, FiZap, FiCalendar, FiMapPin, FiClock, FiArrowRight} = FiIcons;

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-14 sm:pt-16 lg:pt-20">
      {/* Premium Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-500/5 rounded-full blur-3xl"></div>
      </div>

      {/* Celestial Elements */}
      <motion.div
        className="absolute top-32 left-4 sm:left-20 text-blue-400/20"
        animate={{y: [0, -20, 0]}}
        transition={{duration: 4, repeat: Infinity}}
      >
        <SafeIcon icon={FiStar} className="w-4 h-4 sm:w-6 sm:h-6 lg:w-8 lg:h-8" />
      </motion.div>
      <motion.div
        className="absolute top-40 right-4 sm:right-32 text-purple-400/20"
        animate={{y: [0, 20, 0]}}
        transition={{duration: 3, repeat: Infinity}}
      >
        <SafeIcon icon={FiTrendingUp} className="w-3 h-3 sm:w-4 sm:h-4 lg:w-6 lg:h-6" />
      </motion.div>

      <div className="relative z-10 w-full px-2 sm:px-4 lg:px-6 xl:px-8 text-center max-w-none">
        <motion.div
          initial={{opacity: 0, y: 30}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8}}
          className="mt-6 sm:mt-12 lg:mt-20"
        >
          {/* Main Headline - Larger and More Prominent */}
          <motion.h1
            className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl 2xl:text-[10rem] font-light text-white mb-4 sm:mb-6 lg:mb-8 xl:mb-10 tracking-wide leading-[0.9] px-1"
            initial={{opacity: 0, scale: 0.8}}
            animate={{opacity: 1, scale: 1}}
            transition={{duration: 1, delay: 0.2}}
          >
            The{' '}
            <span className="font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent drop-shadow-2xl">
              Bazi
            </span>{' '}
            Blueprint
          </motion.h1>

          {/* Sub-headline */}
          <motion.div
            className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl text-blue-100 mb-4 sm:mb-6 lg:mb-8 font-light px-2"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.4}}
          >
            ศาสตร์แห่งปาจื้อ (八字,Bazi): ศิลปะแห่งการหยั่งรู้จังหวะเพื่อสร้างความได้เปรียบ
          </motion.div>

          {/* Core Promise */}
          <motion.p
            className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl text-slate-300 mb-8 sm:mb-10 lg:mb-12 xl:mb-16 leading-relaxed font-light px-2 sm:px-4 lg:px-0 max-w-5xl mx-auto"
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.8, delay: 0.6}}
          >
            Workshop 3 วัน ที่จะมอบ{' '}
            <span className="text-blue-400 font-medium">"พิมพ์เขียวปาจื้อ (八字)"</span>{' '}
            ส่วนตัว และเปลี่ยนวิธีที่คุณมองโลกและการตัดสินใจไปตลอดกาล
          </motion.p>

          {/* Event Details */}
          <motion.div
            className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 xl:p-10 mb-8 sm:mb-10 lg:mb-12 xl:mb-16 border border-blue-500/20 backdrop-blur-sm mx-2 sm:mx-4 lg:mx-0 max-w-5xl lg:mx-auto"
            initial={{opacity: 0, scale: 0.9}}
            animate={{opacity: 1, scale: 1}}
            transition={{duration: 0.8, delay: 0.8}}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-4 sm:mb-6 lg:mb-8">
              <div className="flex items-center justify-center sm:justify-start space-x-3">
                <SafeIcon icon={FiCalendar} className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-blue-400 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <div className="text-sm sm:text-base lg:text-lg xl:text-xl font-medium text-white">21-23 พ.ย. 2568</div>
                  <div className="text-xs sm:text-sm text-blue-200">3 วันเต็ม Intensive</div>
                </div>
              </div>
              <div className="flex items-center justify-center sm:justify-start space-x-3">
                <SafeIcon icon={FiMapPin} className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-purple-400 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <div className="text-sm sm:text-base lg:text-lg xl:text-xl font-medium text-white">S Ratchada Leisure Hotel</div>
                  <div className="text-xs sm:text-sm text-purple-200">ห้อง Galleria ชั้น 2</div>
                </div>
              </div>
              <div className="flex items-center justify-center sm:justify-start space-x-3 sm:col-span-2 lg:col-span-1">
                <SafeIcon icon={FiClock} className="w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 text-indigo-400 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <div className="text-sm sm:text-base lg:text-lg xl:text-xl font-medium text-white">09.00 - 17.00 น.</div>
                  <div className="text-xs sm:text-sm text-indigo-200">ทุกวัน</div>
                </div>
              </div>
            </div>
            
            <p className="text-base sm:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl text-blue-100 italic font-light leading-relaxed mb-6 sm:mb-8 lg:mb-10 px-1 sm:px-2">
              คุณไม่ได้ค้นพบศาสตร์นี้โดยบังเอิญ... <br />
              แต่ศาสตร์นี้ต่างหากที่เลือกปรากฏต่อผู้ที่พร้อมจะเข้าใจ
            </p>

            {/* Hero CTA Button */}
            <motion.a
              href="https://www.fengshuix.com/event-details/the-bazi-blueprint"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-6 sm:px-8 lg:px-10 xl:px-12 py-4 sm:py-5 lg:py-6 rounded-xl sm:rounded-2xl lg:rounded-3xl text-sm sm:text-base lg:text-lg xl:text-xl font-light shadow-2xl hover:shadow-indigo-500/25 transition-all duration-300 w-full sm:w-auto"
              whileHover={{scale: 1.05, y: -2}}
              whileTap={{scale: 0.95}}
            >
              <span className="text-center">เริ่มการเดินทางแห่งศาสตร์ Bazi</span>
              <SafeIcon icon={FiArrowRight} className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 flex-shrink-0" />
            </motion.a>
          </motion.div>
        </motion.div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 xl:gap-10 mt-10 sm:mt-16 lg:mt-20 mx-2 sm:mx-4 lg:mx-0 max-w-5xl lg:mx-auto"
          initial={{opacity: 0, y: 40}}
          animate={{opacity: 1, y: 0}}
          transition={{duration: 0.8, delay: 1.2}}
        >
          <div className="bg-white/5 backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10">
            <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light text-blue-400 mb-2 sm:mb-3">3</div>
            <div className="text-white/80 font-light text-sm sm:text-base lg:text-lg">วันเต็ม</div>
            <div className="text-white/60 text-xs sm:text-sm">Intensive Workshop</div>
          </div>
          <div className="bg-white/5 backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10">
            <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light text-purple-400 mb-2 sm:mb-3">30</div>
            <div className="text-white/80 font-light text-sm sm:text-base lg:text-lg">ที่นั่งเท่านั้น</div>
            <div className="text-white/60 text-xs sm:text-sm">จำกัดต่อรุ่น</div>
          </div>
          <div className="bg-white/5 backdrop-blur-lg rounded-xl sm:rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/10">
            <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light text-indigo-400 mb-2 sm:mb-3">∞</div>
            <div className="text-white/80 font-light text-sm sm:text-base lg:text-lg">ใช้ได้ตลอดชีวิต</div>
            <div className="text-white/60 text-xs sm:text-sm">Personal Blueprint</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;